import { uuidToColor } from '#lib/helper/color.js';
import {
	type Customer,
	type CustomerStats,
	type CustomerWithBookings,
	type ExportCustomersRequest,
	apiAppBookingCustomerDeletecustomer,
	apiAppBookingCustomerExportcustomers,
	apiAppBookingCustomerGetagregate,
	apiAppBookingCustomerSearchcustomer,
	apiAppBookingCustomerUpsertcustomer
} from '#lib/types/generated/index.js';
import { optimisticDelete, optimisticSave } from '#lib/stores/optimistic.js';
import { derived, get, writable } from 'svelte/store';
import { FormStatus, formStatus } from './form';
import { downloadData } from '#lib/helper/browser.js';

export const customerStore = writable<Array<Customer | CustomerWithBookings>>([]);
export const customerStatsStore = writable<CustomerStats>({
	totalCustomers: 0,
	totalPhoneOptIn: 0,
	totalEmailOptIn: 0,
	averageSpent: 0,
	averageBookings: 0
});

let timeout: NodeJS.Timeout;
export function autocomplete(
	search: string,
	callback: (customers: CustomerWithBookings[]) => void
) {
	clearTimeout(timeout); // debounce
	if (!search || search.trim() === '') {
		return;
	}

	timeout = setTimeout(
		() =>
			apiAppBookingCustomerSearchcustomer({
				search: search,
				limit: 5
			}).then((res) => {
				callback(res.items);
			}),
		400
	);
}

export function loadCustomer(id: string): Promise<void> {
	return apiAppBookingCustomerSearchcustomer({
		id: id,
		limit: 1
	}).then((res) => {
		const items = res.items;
		if (items.length === 0) {
			throw new Error('Customer not found');
		}
		customerStore.set([...get(customerStore).filter((c) => c.id !== id), ...items]);
	});
}

export function addCustomerToStore(
	customer:
		Customer | CustomerWithBookings | Array<Customer | CustomerWithBookings> | null | undefined
) {
	if (!customer) return;
	const items = Array.isArray(customer) ? customer : [customer];
	if (items.length === 0) return;
	const newIds = new Set(items.map((c) => c.id));
	customerStore.update((current) => current.filter((c) => !newIds.has(c.id)).concat(items));
}

export const getCustomerColor = derived(
	[uuidToColor],
	([$uuidToColor]) =>
		(customer: Customer | undefined) => {
			if (!customer) return '';
			return $uuidToColor(customer.id);
		}
);

export function exportCustomer(params: ExportCustomersRequest) {
	formStatus.set(FormStatus.Processing);
	apiAppBookingCustomerExportcustomers(params)
		.then((response) => {
			// Download the file
			downloadData(response, 'export.csv');
			formStatus.set(FormStatus.Success);
		})
		.catch(() => {
			formStatus.set(FormStatus.Error);
		});
}

export function saveCustomer(customer: Customer): Promise<Customer> {
	return optimisticSave(customerStore, customer, () =>
		apiAppBookingCustomerUpsertcustomer(customer)
	);
}

export function deleteCustomer(customer: Customer): Promise<void> {
	return optimisticDelete(customerStore, customer, () =>
		apiAppBookingCustomerDeletecustomer(customer.id)
	);
}

export async function fetchCustomerStats(): Promise<CustomerStats> {
	const stats = await apiAppBookingCustomerGetagregate();
	customerStatsStore.set(stats);
	return stats;
}
